import "./CategoryNavigation.scss";

import React from "react";
import { Link } from "react-router";
import CategoryFilterItem from "./CategoryFilterItem";
import ShortenedCategoryList from "./ShortenedCategoryList";

import { SearchQuery } from "shared/src/types/articlesearch/SearchResult";
import { Category, CategoryRoot } from "shared/src/types/articlesearch/Category";
import { drillDownToCategory, sortCategoriesByAmounts } from "../../../../utils/categoryUtils";

import CountryUtil from "shared/src/uidispatcherapi/CountryUtil";
import { FormattedMessage } from "react-intl";

function CategoryTree(props: { category: Category; query: SearchQuery; queryLinkComponent: any }) {
    const category = props.category;
    const subCategoryList = (
        <ShortenedCategoryList
            categories={sortCategoriesByAmounts(Object.values(category.children))}
            query={props.query}
            queryLinkComponent={props.queryLinkComponent}
        />
    );
    return (
        <div className="category-filter-item" role="region" aria-label="Filter items">
            <h4>
                <CategoryFilterItem
                    categoryName={category.name}
                    categoryAmounts={category.amounts}
                    urlCategoryPath={category.urlCategoryPath}
                    query={props.query}
                    queryLinkComponent={props.queryLinkComponent}
                />
            </h4>
            {subCategoryList}
        </div>
    );
}

function CategoryTreeRoot(props: { categories: CategoryRoot; query: SearchQuery; queryLinkComponent: any }) {
    const category: CategoryRoot | Category = drillDownToCategory(props.categories, props.query.categoryPath);
    const categoriesToShow: Category[] = category.name ? [category] : Object.values(category.children);
    const sortedCategories = sortCategoriesByAmounts(categoriesToShow);
    return (
        <div className="first-level-categories">
            {sortedCategories.map((cat, key) => (
                <CategoryTree
                    key={(cat.categoryId || "") + key}
                    category={cat}
                    query={props.query}
                    queryLinkComponent={props.queryLinkComponent}
                />
            ))}
        </div>
    );
}

type categoryBreadcrumbsProps = {
    categoryPath: string | undefined | null;
    categories: CategoryRoot;
    query: SearchQuery;
    doNotShowAllCategoriesLink: boolean | undefined | null;
    queryLinkComponent: any;
    itemPrefix?: JSX.Element;
    leaveOutFirstItemPrefix?: boolean;
    rootName?: JSX.Element;
    includeLastCategory?: boolean;
    header?: boolean;
};

function CategoryBreadcrumbs(props: categoryBreadcrumbsProps) {
    let categoryPath = props.categoryPath;
    if (!categoryPath) {
        // show drilled-down path (skipping uninteresting categories)
        categoryPath = drillDownToCategory(props.categories, undefined).urlCategoryPath;
    }
    if (!categoryPath) {
        return null;
    }

    const parts = categoryPath.split("/");
    const breadcrumbs = [];
    let lastCategory: CategoryRoot | Category | undefined;
    for (let i = 0; i < parts.length + (props.includeLastCategory ? 1 : 0); ++i) {
        const pathPrefix = parts.slice(0, i);
        const pathPrefixString = pathPrefix.join("/");
        const category: CategoryRoot | Category | undefined = lastCategory
            ? Object.values(lastCategory.children).find((cat: Category) => cat.urlCategoryPath === pathPrefixString)
            : props.categories;
        if (category && !(props.doNotShowAllCategoriesLink && i === 0)) {
            breadcrumbs.push(
                <li key={pathPrefixString}>
                    <CategoryFilterItem
                        className="ex-category-breadcrumb"
                        categoryName={category.name}
                        categoryAmounts={undefined}
                        urlCategoryPath={pathPrefixString}
                        query={props.query}
                        queryLinkComponent={props.queryLinkComponent}
                        itemPrefix={i === 0 && props.leaveOutFirstItemPrefix ? <span></span> : props.itemPrefix}
                        rootName={props.rootName}
                        header={props.header}
                    />
                </li>
            );
        }
        lastCategory = category;
    }
    if (breadcrumbs.length === 0) {
        return null;
    } else {
        return <ul className="ex-category-breadcrumbs back-link">{breadcrumbs}</ul>;
    }
}

function CategoryHeaderBreadcrumbs(props: categoryBreadcrumbsProps) {
    const headerProps = {
        itemPrefix: <span className="icon-small-chevron-right" />,
        leaveOutFirstItemPrefix: true,
        rootName: (
            <span>
                {
                    <FormattedMessage
                        id="SD_HEADER_BREADCRUMBS_ROOT"
                        defaultMessage={CountryUtil.getSalesLine().toUpperCase()}
                    />
                }
            </span>
        ),
        includeLastCategory: true,
        header: true
    };
    return CategoryBreadcrumbs({ ...props, ...headerProps });
}

function SearchHeaderBreadcrumbs(props: { query: SearchQuery }) {
    return (
        <ul className="ex-search-breadcrumbs">
            <li>
                <Link to="/">
                    {
                        <FormattedMessage
                            id="SD_HEADER_BREADCRUMBS_ROOT"
                            defaultMessage={CountryUtil.getSalesLine().toUpperCase()}
                        />
                    }
                </Link>
            </li>
            <span className="icon-small-chevron-right" />
            <li>
                <FormattedMessage id="SD_SEARCH_RESULTS_FOR" values={{ q: props.query.q }} />
            </li>
        </ul>
    );
}

const exported = {
    CategoryTree,
    CategoryTreeRoot,
    CategoryBreadcrumbs,
    CategoryHeaderBreadcrumbs,
    SearchHeaderBreadcrumbs
};

export default exported;
export { CategoryTree, CategoryTreeRoot, CategoryBreadcrumbs, CategoryHeaderBreadcrumbs, SearchHeaderBreadcrumbs };
